"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var PearceKellyDetector_1 = require("./PearceKellyDetector");
var util_1 = require("./util");
function bind(fn, context) {
    return fn.bind(context);
}
var GraphlibAdapter = (function () {
    function GraphlibAdapter(g, detector, graphlib) {
        this.graphlib = graphlib;
        this.g = g;
        this.detector = detector;
        this.adapter = {
            getData: bind(this.getData, this),
            getPredecessorsOf: bind(this.getPredecessorsOf, this),
            getSuccessorsOf: bind(this.getSuccessorsOf, this),
        };
    }
    GraphlibAdapter.create = function (options) {
        var g = new options.graphlib(util_1.assign(options.graphOptions || {}, { directed: true }));
        var detector = options.cycleDetector || new PearceKellyDetector_1.PearceKellyDetector();
        return new GraphlibAdapter(g, detector, options.graphlib);
    };
    GraphlibAdapter.prototype.map = function (vertexDataMapper, edgeDataMapper) {
        var clonedG = new this.graphlib({
            compound: this.g.isCompound(),
            directed: this.g.isDirected(),
            multigraph: this.g.isMultigraph(),
        });
        for (var _i = 0, _a = this.g.nodes(); _i < _a.length; _i++) {
            var node = _a[_i];
            var vertexData = this.g.node(node);
            var partialVertexData = vertexDataMapper(vertexData);
            if (partialVertexData.gid === undefined) {
                partialVertexData.gid = vertexData.gid;
            }
            partialVertexData.order = vertexData.order;
            partialVertexData.visited = vertexData.visited;
            clonedG.setNode(partialVertexData.gid, partialVertexData);
        }
        for (var _b = 0, _c = this.g.edges(); _b < _c.length; _b++) {
            var edge = _c[_b];
            var edgeData = this.g.edge(edge);
            clonedG.setEdge(edge.v, edge.w, edgeData !== undefined ? edgeDataMapper(edgeData) : undefined, edge.name);
        }
        var clonedDetector = this.detector.map();
        return new GraphlibAdapter(clonedG, clonedDetector, this.graphlib);
    };
    GraphlibAdapter.prototype.clone = function (vertexDataCloner, edgeDataCloner) {
        var vCloner = vertexDataCloner !== undefined ? vertexDataCloner : function (vertex) { return vertex; };
        var eCloner = edgeDataCloner !== undefined ? edgeDataCloner : function (data) { return data; };
        return this.map(vCloner, eCloner);
    };
    GraphlibAdapter.prototype.canContractEdge = function (from, to) {
        return util_1.canContractEdge(this, from, to);
    };
    GraphlibAdapter.prototype.contractEdge = function (from, to, vertexMerger, edgeMerger) {
        return util_1.contractEdge(this, from, to, vertexMerger, edgeMerger);
    };
    GraphlibAdapter.prototype.isReachable = function (source, target) {
        return this.detector.isReachable(this.adapter, source, target);
    };
    GraphlibAdapter.prototype.getSuccessorsOf = function (vertex) {
        var _this = this;
        var nodes = this.g.successors(vertex.gid);
        if (!nodes) {
            return util_1.EmptyIterator;
        }
        return util_1.createMappedArrayIterator(nodes, function (node) { return _this.g.node(node); });
    };
    GraphlibAdapter.prototype.getPredecessorsOf = function (vertex) {
        var _this = this;
        var nodes = this.g.predecessors(vertex.gid);
        if (!nodes) {
            return util_1.EmptyIterator;
        }
        return util_1.createMappedArrayIterator(nodes, function (node) { return _this.g.node(node); });
    };
    GraphlibAdapter.prototype.hasEdge = function (from, to) {
        return this.g.hasEdge(from.gid, to.gid);
    };
    GraphlibAdapter.prototype.hasVertex = function (vertex) {
        return this.g.hasNode(vertex.gid);
    };
    GraphlibAdapter.prototype.getVertexCount = function () {
        return this.g.nodeCount();
    };
    GraphlibAdapter.prototype.getEdgeCount = function () {
        return this.g.edgeCount();
    };
    GraphlibAdapter.prototype.getEdgesWithDataFrom = function (vertex) {
        var _this = this;
        var edges = this.g.outEdges(vertex.gid);
        if (edges === undefined) {
            return util_1.EmptyIterator;
        }
        return util_1.createMappedArrayIterator(edges, function (edge) { return [
            _this.g.node(edge.w),
            _this.g.edge(edge.v, edge.w)
        ]; });
    };
    GraphlibAdapter.prototype.getEdgeDataFrom = function (vertex) {
        var _this = this;
        var edges = this.g.outEdges(vertex.gid);
        if (edges === undefined) {
            return util_1.EmptyIterator;
        }
        return util_1.createFilteredIterator(util_1.createMappedArrayIterator(edges, function (edge) { return _this.g.edge(edge.v, edge.w); }));
    };
    GraphlibAdapter.prototype.getEdgesWithDataTo = function (vertex) {
        var _this = this;
        var edges = this.g.inEdges(vertex.gid);
        if (edges === undefined) {
            return util_1.EmptyIterator;
        }
        return util_1.createMappedArrayIterator(edges, function (edge) { return [
            _this.g.node(edge.v),
            _this.g.edge(edge.v, edge.w)
        ]; });
    };
    GraphlibAdapter.prototype.getEdgeDataTo = function (vertex) {
        var _this = this;
        var edges = this.g.inEdges(vertex.gid);
        if (edges === undefined) {
            return util_1.EmptyIterator;
        }
        return util_1.createFilteredIterator(util_1.createMappedArrayIterator(edges, function (edge) { return _this.g.edge(edge.v, edge.w); }));
    };
    GraphlibAdapter.prototype.getEdgeData = function (from, to) {
        return this.g.edge(from.gid, to.gid);
    };
    GraphlibAdapter.prototype.setEdgeData = function (from, to, data) {
        if (!this.g.hasEdge(from.gid, to.gid)) {
            return false;
        }
        this.g.setEdge(from.gid, to.gid, data);
        return true;
    };
    GraphlibAdapter.prototype.getVertices = function () {
        var _this = this;
        return util_1.createMappedArrayIterator(this.g.nodes(), function (node) { return _this.g.node(node); });
    };
    GraphlibAdapter.prototype.getEdges = function () {
        var _this = this;
        return util_1.createMappedArrayIterator(this.g.edges(), function (edge) {
            return [_this.g.node(edge.v), _this.g.node(edge.w)];
        });
    };
    GraphlibAdapter.prototype.getEdgesWithData = function () {
        var _this = this;
        return util_1.createMappedArrayIterator(this.g.edges(), function (edge) {
            return [
                _this.g.node(edge.v),
                _this.g.node(edge.w),
                _this.g.edge(edge.v, edge.w)
            ];
        });
    };
    GraphlibAdapter.prototype.supportsOrder = function () {
        return this.detector.supportsOrder();
    };
    GraphlibAdapter.prototype.getOrder = function (vertex) {
        return this.detector.getOrder(this.adapter, vertex);
    };
    Object.defineProperty(GraphlibAdapter.prototype, "graph", {
        get: function () {
            return this.g;
        },
        enumerable: true,
        configurable: true
    });
    GraphlibAdapter.prototype.canAddEdge = function (from, to) {
        if (this.g.hasEdge(from.gid, to.gid)) {
            return false;
        }
        var didNotHaveFrom = this.addVertex(from);
        var didNotHaveTo = this.addVertex(to);
        if (!this.detector.canAddEdge(this.adapter, from, to)) {
            if (didNotHaveFrom) {
                this.deleteVertex(from);
            }
            if (didNotHaveTo) {
                this.deleteVertex(to);
            }
            return false;
        }
        return true;
    };
    GraphlibAdapter.prototype.addEdge = function (from, to, data) {
        if (this.g.hasEdge(from.gid, to.gid)) {
            return false;
        }
        var didNotHaveFrom = this.addVertex(from);
        var didNotHaveTo = this.addVertex(to);
        if (!this.detector.canAddEdge(this.adapter, from, to)) {
            if (didNotHaveFrom) {
                this.deleteVertex(from);
            }
            if (didNotHaveTo) {
                this.deleteVertex(to);
            }
            return false;
        }
        this.g.setEdge(from.gid, to.gid, data);
        return true;
    };
    GraphlibAdapter.prototype.createVertex = function (data) {
        var vertexData = this.detector.createVertexData(this.adapter);
        return util_1.assign(vertexData, data);
    };
    GraphlibAdapter.prototype.addVertex = function (vertex) {
        if (this.hasVertex(vertex)) {
            return false;
        }
        this.g.setNode(vertex.gid, vertex);
        return true;
    };
    GraphlibAdapter.prototype.deleteEdge = function (from, to) {
        if (!this.g.hasEdge(from.gid, to.gid)) {
            return false;
        }
        this.g.removeEdge(from.gid, to.gid);
        return true;
    };
    GraphlibAdapter.prototype.deleteVertex = function (vertex) {
        if (!this.g.hasNode(vertex.gid)) {
            return false;
        }
        this.detector.onVertexDeletion(this.adapter, vertex);
        this.g.removeNode(vertex.gid);
        return true;
    };
    GraphlibAdapter.prototype.getData = function (vertex) {
        return this.g.node(vertex.gid);
    };
    return GraphlibAdapter;
}());
exports.GraphlibAdapter = GraphlibAdapter;
